import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  ReceiptLong as ReceiptIcon,
  Search as SearchIcon,
  AccountBalance as BankIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  CreditCard as CreditCardIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  InfoOutlined as InfoIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import ClientInvoicePrintModal from './ClientInvoicePrintModal';
import ClientPaymentModal from './ClientPaymentModal';

export default function ClientInvoicesTab({
  invoices = [],
  loading = false,
  clientId,
  currentUser,
  onInvoiceUpdated
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Modals state
  const [selectedInvoiceForPrint, setSelectedInvoiceForPrint] = useState(null);
  const [selectedInvoiceForPay, setSelectedInvoiceForPay] = useState(null);

  // Copy feedback state
  const [copiedKey, setCopiedKey] = useState(null);

  const handleCopy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  // Metrics computation
  const metrics = useMemo(() => {
    let totalInvoiced = 0;
    let totalSettled = 0;
    let totalBalanceDue = 0;

    invoices.forEach(inv => {
      totalInvoiced += Number(inv.totalAmount || 0);
      totalSettled += Number(inv.paidAmount || 0);
      totalBalanceDue += Number(inv.balanceDue || 0);
    });

    return { totalInvoiced, totalSettled, totalBalanceDue };
  }, [invoices]);

  // Filtering
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      // Status filter
      if (statusFilter === 'PAID') {
        if (inv.status !== 'paid' && Number(inv.balanceDue) > 0) return false;
      } else if (statusFilter === 'PARTIAL') {
        if (inv.status !== 'partially_paid') return false;
      } else if (statusFilter === 'UNPAID') {
        if (inv.status === 'paid' || Number(inv.balanceDue) <= 0) return false;
      } else if (statusFilter === 'UTR_PENDING') {
        if (!inv.hasPendingClientUtr && (!inv.clientUtrSubmission || inv.clientUtrSubmission.verified)) return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const numMatch = inv.invoiceNumber?.toLowerCase().includes(q);
        const milestoneMatch = inv.milestoneTitle?.toLowerCase().includes(q);
        const utrMatch = inv.clientUtrSubmission?.utrNumber?.toLowerCase().includes(q);
        const itemsMatch = (inv.items || []).some(it => it.description?.toLowerCase().includes(q));
        if (!numMatch && !milestoneMatch && !utrMatch && !itemsMatch) return false;
      }

      return true;
    });
  }, [invoices, statusFilter, searchQuery]);

  const formatMoney = (val) => {
    return `₹${Number(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return (
    <Box>
      {/* 1. Summary Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 3.5 }}>
        {/* Total Invoiced */}
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total Invoiced
              </Typography>
              <ReceiptIcon sx={{ color: '#06b6d4', fontSize: 20 }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
              {formatMoney(metrics.totalInvoiced)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.5 }}>
              Cumulative milestone and service value
            </Typography>
          </Paper>
        </Grid>

        {/* Verified & Settled */}
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: 'rgba(16, 185, 129, 0.04)',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total Settled & Verified
              </Typography>
              <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#34d399', letterSpacing: '-0.02em' }}>
              {formatMoney(metrics.totalSettled)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.5 }}>
              Reconciled with corporate IndusInd Bank account
            </Typography>
          </Paper>
        </Grid>

        {/* Balance Due */}
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: metrics.totalBalanceDue > 0 ? 'rgba(239, 68, 68, 0.04)' : 'rgba(255, 255, 255, 0.02)',
              border: metrics.totalBalanceDue > 0 ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ color: metrics.totalBalanceDue > 0 ? '#f87171' : '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Outstanding Balance Due
              </Typography>
              <ScheduleIcon sx={{ color: metrics.totalBalanceDue > 0 ? '#f87171' : '#94A3B8', fontSize: 20 }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: metrics.totalBalanceDue > 0 ? '#f87171' : '#FFFFFF', letterSpacing: '-0.02em' }}>
              {formatMoney(metrics.totalBalanceDue)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.5 }}>
              {metrics.totalBalanceDue > 0 ? 'Payable via direct Wire or UPI' : 'All accounts fully current and settled ✓'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* 2. Official IndusInd Bank Remittance Box */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 3.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(16, 185, 129, 0.05) 100%)',
          border: '1px solid rgba(6, 182, 212, 0.25)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: 'rgba(6, 182, 212, 0.15)',
                border: '1px solid rgba(6, 182, 212, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BankIcon sx={{ color: '#06b6d4', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#FFFFFF', fontSize: '0.98rem' }}>
                Official Bank Remittance Coordinates
              </Typography>
              <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.82rem' }}>
                Direct Wire Transfer (NEFT / RTGS / IMPS) & Instant UPI Remittance
              </Typography>
            </Box>
          </Box>
          <Chip
            label="ZERO FEES • DIRECT CLEARING"
            size="small"
            sx={{
              bgcolor: 'rgba(16, 185, 129, 0.15)',
              color: '#34d399',
              fontWeight: 800,
              fontSize: '10px',
              border: '1px solid rgba(16, 185, 129, 0.35)'
            }}
          />
        </Box>

        <Grid container spacing={2}>
          {/* Bank & Beneficiary */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ p: 1.8, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '10px', textTransform: 'uppercase', fontWeight: 700 }}>
                Beneficiary Name
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, mt: 0.3, fontSize: '0.86rem' }}>
                VERVEAILABS PRIVATE LIMITED
              </Typography>
              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', fontSize: '11px', mt: 0.2 }}>
                Bank: <strong>IndusInd Bank</strong>
              </Typography>
            </Box>
          </Grid>

          {/* Account Number */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ p: 1.8, borderRadius: 2, bgcolor: 'rgba(6, 182, 212, 0.06)', border: '1px solid rgba(6, 182, 212, 0.25)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#06b6d4', display: 'block', fontSize: '10px', textTransform: 'uppercase', fontWeight: 800 }}>
                  Current Account Number
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 800, fontFamily: 'monospace', mt: 0.2, fontSize: '0.95rem' }}>
                  251401230904
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', fontSize: '10px' }}>
                  Branch: Bengaluru
                </Typography>
              </Box>
              <Tooltip title={copiedKey === 'acc' ? 'Copied!' : 'Copy A/C No'}>
                <IconButton size="small" onClick={() => handleCopy('acc', '251401230904')} sx={{ color: copiedKey === 'acc' ? '#10b981' : '#22d3ee' }}>
                  {copiedKey === 'acc' ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>

          {/* IFSC Code */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ p: 1.8, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '10px', textTransform: 'uppercase', fontWeight: 700 }}>
                  IFSC Code
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 800, fontFamily: 'monospace', mt: 0.2, fontSize: '0.95rem' }}>
                  INDB0000866
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', fontSize: '10px' }}>
                  RTGS / NEFT / IMPS Valid
                </Typography>
              </Box>
              <Tooltip title={copiedKey === 'ifsc' ? 'Copied!' : 'Copy IFSC Code'}>
                <IconButton size="small" onClick={() => handleCopy('ifsc', 'INDB0000866')} sx={{ color: copiedKey === 'ifsc' ? '#10b981' : '#94A3B8' }}>
                  {copiedKey === 'ifsc' ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>

          {/* UPI ID */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ p: 1.8, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '10px', textTransform: 'uppercase', fontWeight: 700 }}>
                  UPI Virtual ID
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 800, fontFamily: 'monospace', mt: 0.2, fontSize: '0.86rem' }}>
                  251401230904@indus
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', fontSize: '10px' }}>
                  GPay, PhonePe, Paytm, BHIM
                </Typography>
              </Box>
              <Tooltip title={copiedKey === 'upi' ? 'Copied!' : 'Copy UPI ID'}>
                <IconButton size="small" onClick={() => handleCopy('upi', '251401230904@indus')} sx={{ color: copiedKey === 'upi' ? '#10b981' : '#94A3B8' }}>
                  {copiedKey === 'upi' ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 3. Filter and Search Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {[
            { id: 'ALL', label: `All Invoices (${invoices.length})` },
            { id: 'UNPAID', label: 'Due / Unpaid' },
            { id: 'PARTIAL', label: 'Partially Paid' },
            { id: 'PAID', label: 'Settled & Paid' },
            { id: 'UTR_PENDING', label: 'UTR Submitted' },
          ].map(tab => (
            <Chip
              key={tab.id}
              label={tab.label}
              onClick={() => setStatusFilter(tab.id)}
              sx={{
                bgcolor: statusFilter === tab.id ? 'rgba(6, 182, 212, 0.2)' : 'rgba(255, 255, 255, 0.03)',
                color: statusFilter === tab.id ? '#22d3ee' : '#94A3B8',
                fontWeight: statusFilter === tab.id ? 800 : 600,
                fontSize: '12px',
                border: statusFilter === tab.id ? '1px solid rgba(6, 182, 212, 0.5)' : '1px solid rgba(255, 255, 255, 0.08)',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(6, 182, 212, 0.15)' }
              }}
            />
          ))}
        </Box>

        <Box sx={{ minWidth: { xs: '100%', sm: 260 } }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by invoice #, milestone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#64748B', fontSize: 18, mr: 1 }} />
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#FFFFFF',
                bgcolor: 'rgba(255, 255, 255, 0.03)',
                fontSize: '13px',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.08)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
              }
            }}
          />
        </Box>
      </Box>

      {/* 4. Invoices List / Card Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#06b6d4' }} />
        </Box>
      ) : filteredInvoices.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.02)',
            border: '1px dashed rgba(255, 255, 255, 0.1)',
            borderRadius: 3
          }}
        >
          <ReceiptIcon sx={{ fontSize: 48, color: '#64748B', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
            {searchQuery || statusFilter !== 'ALL' ? 'No Matching Invoices Found' : 'No Invoices Issued Yet'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#94A3B8', maxWidth: 500, mx: 'auto' }}>
            {searchQuery || statusFilter !== 'ALL'
              ? 'Try adjusting your search criteria or switching the filter to "All Invoices".'
              : 'As project milestones and architectural deliverables are reached, official tax invoices will be generated and published here.'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          {filteredInvoices.map((inv) => {
            const isPaid = inv.status === 'paid' || Number(inv.balanceDue) <= 0;
            const isPartial = inv.status === 'partially_paid';
            const hasUtr = inv.clientUtrSubmission && !inv.clientUtrSubmission.verified;

            return (
              <Paper
                key={inv.id}
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.02)',
                  border: isPaid
                    ? '1px solid rgba(16, 185, 129, 0.25)'
                    : hasUtr
                    ? '1px solid rgba(59, 130, 246, 0.35)'
                    : '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: 'rgba(6, 182, 212, 0.35)'
                  }
                }}
              >
                <Grid container spacing={2.5} alignItems="center">
                  {/* Left Column: Number, Title, Dates */}
                  <Grid item xs={12} md={5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#22d3ee', fontFamily: 'monospace' }}>
                        {inv.invoiceNumber}
                      </Typography>
                      <Chip
                        label={
                          isPaid
                            ? 'PAID / SETTLED ✓'
                            : hasUtr
                            ? 'UTR PENDING AUDIT'
                            : isPartial
                            ? 'PARTIALLY PAID'
                            : 'ISSUED / UNPAID'
                        }
                        size="small"
                        sx={{
                          bgcolor: isPaid
                            ? 'rgba(16, 185, 129, 0.15)'
                            : hasUtr
                            ? 'rgba(59, 130, 246, 0.15)'
                            : isPartial
                            ? 'rgba(245, 158, 11, 0.15)'
                            : 'rgba(6, 182, 212, 0.15)',
                          color: isPaid
                            ? '#34d399'
                            : hasUtr
                            ? '#60a5fa'
                            : isPartial
                            ? '#fbbf24'
                            : '#22d3ee',
                          fontWeight: 800,
                          fontSize: '10px',
                          border: `1px solid ${
                            isPaid
                              ? 'rgba(16, 185, 129, 0.4)'
                              : hasUtr
                              ? 'rgba(59, 130, 246, 0.4)'
                              : isPartial
                              ? 'rgba(245, 158, 11, 0.4)'
                              : 'rgba(6, 182, 212, 0.4)'
                          }`
                        }}
                      />
                    </Box>

                    <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                      {inv.milestoneTitle || (inv.items && inv.items[0]?.description) || 'AI Systems Development & Delivery'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, fontSize: '11px', color: '#94A3B8' }}>
                      <span><strong>Issued:</strong> {inv.issueDate}</span>
                      <span><strong>Due:</strong> {inv.dueDate}</span>
                      {inv.items?.length > 1 && <span>• {inv.items.length} line items</span>}
                    </Box>
                  </Grid>

                  {/* Middle Column: Financials */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '10px', textTransform: 'uppercase' }}>
                          Invoice Total
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: '#FFFFFF', fontWeight: 800 }}>
                          {formatMoney(inv.totalAmount)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" sx={{ color: '#10b981', display: 'block', fontSize: '10px', textTransform: 'uppercase' }}>
                          Settled
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: '#34d399', fontWeight: 800 }}>
                          {formatMoney(inv.paidAmount || 0)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" sx={{ color: isPaid ? '#10b981' : '#f87171', display: 'block', fontSize: '10px', textTransform: 'uppercase' }}>
                          Balance Due
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: isPaid ? '#10b981' : '#f87171', fontWeight: 800 }}>
                          {formatMoney(inv.balanceDue || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Right Column: Actions */}
                  <Grid item xs={12} sm={6} md={3} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                    <Box sx={{ display: 'inline-flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PrintIcon sx={{ fontSize: '15px !important' }} />}
                        onClick={() => setSelectedInvoiceForPrint(inv)}
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.15)',
                          color: '#FFFFFF',
                          bgcolor: 'rgba(255, 255, 255, 0.03)',
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)', borderColor: 'rgba(255, 255, 255, 0.3)' }
                        }}
                      >
                        View / Print
                      </Button>

                      {!isPaid && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CreditCardIcon sx={{ fontSize: '15px !important' }} />}
                          onClick={() => setSelectedInvoiceForPay(inv)}
                          sx={{
                            background: hasUtr
                              ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
                              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: '#FFFFFF',
                            fontSize: '12px',
                            fontWeight: 700,
                            textTransform: 'none',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                            '&:hover': {
                              background: hasUtr
                                ? 'linear-gradient(135deg, #0369a1 0%, #075985 100%)'
                                : 'linear-gradient(135deg, #34d399 0%, #047857 100%)'
                            }
                          }}
                        >
                          {hasUtr ? 'Update UTR' : 'Pay / Submit UTR'}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                {/* UTR Submission Pending Alert Banner */}
                {hasUtr && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 1.5,
                      borderTop: '1px solid rgba(59, 130, 246, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexWrap: 'wrap'
                    }}
                  >
                    <InfoIcon sx={{ color: '#60a5fa', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#93c5fd', fontSize: '0.82rem' }}>
                      <strong>Bank UTR Submitted:</strong> Ref #{inv.clientUtrSubmission.utrNumber} (Amount: ₹{Number(inv.clientUtrSubmission.amount || inv.balanceDue).toLocaleString('en-IN')}) via {inv.clientUtrSubmission.paymentMode || 'Wire'}.
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B', fontSize: '11px' }}>
                      Verve AI Accounts desk is reconciling with IndusInd Bank statement.
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Connected Modals */}
      {selectedInvoiceForPrint && (
        <ClientInvoicePrintModal
          open={Boolean(selectedInvoiceForPrint)}
          onClose={() => setSelectedInvoiceForPrint(null)}
          invoice={selectedInvoiceForPrint}
          onOpenPayment={(inv) => setSelectedInvoiceForPay(inv)}
        />
      )}

      {selectedInvoiceForPay && (
        <ClientPaymentModal
          open={Boolean(selectedInvoiceForPay)}
          onClose={() => setSelectedInvoiceForPay(null)}
          invoice={selectedInvoiceForPay}
          clientId={clientId}
          currentUser={currentUser}
          onPaymentSubmitted={(updatedInv) => {
            if (onInvoiceUpdated) {
              onInvoiceUpdated(updatedInv);
            }
          }}
        />
      )}
    </Box>
  );
}
